import { useEffect, useRef, useState } from 'react'

import type {
    CustomerDisplayCartEvent,
    CustomerDisplayCheckoutEvent,
    CustomerDisplayPaidEvent,
    CustomerDisplayQREvent,
} from '@/lib/schemas/customer-display-event-schemas'
import type { SseValidationErrorHandler } from '@/lib/schemas/sse-event-schemas'
import { SSE_URL } from '@/lib/config'
import { createCustomerDisplayEventSchemas } from '@/lib/schemas/customer-display-event-schemas'
import { createSseListener } from '@/lib/schemas/sse-event-schemas'
import { subscribeSse } from '@/lib/sse-stream-registry'

export type {
    CustomerDisplayCartEvent,
    CustomerDisplayCartItem,
    CustomerDisplayCheckoutEvent,
    CustomerDisplayPaidEvent,
    CustomerDisplayQREvent,
} from '@/lib/schemas/customer-display-event-schemas'

export interface CustomerDisplayHandlers {
    onShowQR?: (event: CustomerDisplayQREvent) => void
    onPaid?: (event: CustomerDisplayPaidEvent) => void
    onCart?: (event: CustomerDisplayCartEvent) => void
    onCheckout?: (event: CustomerDisplayCheckoutEvent) => void
    onClear?: () => void
    onValidationError?: SseValidationErrorHandler
}

/** The shared stream owns reconnect/auth; only validated events reach display state. */
export function useCustomerDisplaySSE(handlers: CustomerDisplayHandlers = {}) {
    const [connected, setConnected] = useState(false)
    const handlersRef = useRef(handlers)
    handlersRef.current = handlers

    useEffect(() => {
        const schemas = createCustomerDisplayEventSchemas()
        const onInvalid: SseValidationErrorHandler = (failure) =>
            handlersRef.current.onValidationError?.(failure)
        return subscribeSse({
            url: SSE_URL,
            onConnectedChange: setConnected,
            listeners: {
                customer_display_qr: createSseListener(
                    'customer_display_qr',
                    schemas.customer_display_qr,
                    (data) => handlersRef.current.onShowQR?.(data),
                    onInvalid,
                ),
                customer_display_paid: createSseListener(
                    'customer_display_paid',
                    schemas.customer_display_paid,
                    (data) => handlersRef.current.onPaid?.(data),
                    onInvalid,
                ),
                customer_display_cart: createSseListener(
                    'customer_display_cart',
                    schemas.customer_display_cart,
                    (data) => handlersRef.current.onCart?.(data),
                    onInvalid,
                ),
                customer_display_checkout: createSseListener(
                    'customer_display_checkout',
                    schemas.customer_display_checkout,
                    (data) => handlersRef.current.onCheckout?.(data),
                    onInvalid,
                ),
                customer_display_clear: createSseListener(
                    'customer_display_clear',
                    schemas.customer_display_clear,
                    () => handlersRef.current.onClear?.(),
                    onInvalid,
                ),
            },
        })
    }, [])

    return { connected }
}
